import { type ColumnDef } from "@tanstack/react-table"
import { ExternalLink } from "lucide-react"

import type { IComputedCorrespondence } from "@apis/accounts/types"
import { Button } from "@components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip"
import { formatDate } from "@utils/formatters"
import { DateCell } from "@utils/table/columnFactories"

import { ViewLetterButton } from "./ViewLetterButton"
import { SendMailButton } from "./SendMailButton"
import { SendEmailButton } from "./SendEmailButton"

export const correspondenceColumns: ColumnDef<IComputedCorrespondence>[] = [
  {
    header: "Type",
    cell: ({ row }) => {
      const c = row.original
      return (
        <span>
          {c.custom_letter_title || c.description || c.correspondence_type_code}
        </span>
      )
    },
  },
  {
    id: "view",
    header: "View",
    cell: ({ row }) => {
      const c = row.original

      const hasDocument = c.url || c.disclosure || c.archive_loc

      if (!hasDocument && !c.isWelcome) return null

      return c.url ? (
        <Tooltip>
          <TooltipTrigger>
            <Button asChild size="icon" variant="ghost">
              <a href={c.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="tw:h-4 tw:w-4" />
              </a>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">View</TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger>
            <ViewLetterButton
              account_id={c.account_id}
              account_correspondence_id={c.account_correspondence_id}
              isWelcome={c.isWelcome}
            />
          </TooltipTrigger>
          <TooltipContent side="right">View</TooltipContent>
        </Tooltip>
      )
    },
  },
  {
    id: "send",
    header: "Send",
    cell: ({ row }) => {
      const c = row.original

      const correspondenceName =
        c.custom_letter_title || c.description || c.correspondence_type_code

      return (
        <div className="tw:flex tw:gap-1">
          <Tooltip>
            <TooltipTrigger>
              <SendMailButton
                account_correspondence_id={c.account_correspondence_id}
                account_id={c.account_id}
                isWelcome={c.isWelcome}
                correspondence_name={correspondenceName}
              />
            </TooltipTrigger>
            <TooltipContent side="right">Send via USPS</TooltipContent>
          </Tooltip>
          {c.can_resend_via_email && (
            <Tooltip>
              <TooltipTrigger>
                <SendEmailButton
                  account_correspondence_id={c.account_correspondence_id}
                  account_id={c.account_id}
                  isWelcome={c.isWelcome}
                  correspondence_name={correspondenceName}
                />
              </TooltipTrigger>
              <TooltipContent side="right">Send via Email</TooltipContent>
            </Tooltip>
          )}
        </div>
      )
    },
  },
  {
    accessorKey: "last_resent_dt",
    header: "Last Resent",
    cell: (props) => <DateCell {...props} />,
  },
  {
    id: "initiallySent",
    header: "Initially Sent",
    accessorFn: (row) => ({
      status: row.batch_status_code,
      date: row.queued_dt,
    }),
    cell: ({ getValue }) => {
      const { status, date } = getValue() as {
        status: string
        date: string
      }

      const formattedDate = formatDate(date)

      return (
        <>
          {status} {formattedDate && `on ${formattedDate}`}
        </>
      )
    },
  },
]
